import { useNavigate, Link } from 'react-router-dom';
import { AlertCircle, Wind, Activity, Layers, Waves, Heart, Youtube, Brain, Sparkles, Target, ClipboardList, ArrowUpRight } from 'lucide-react';

// =========================================================
// PREVIEW PAGE — Direction 3 "Sérénité Éditoriale"
// Same content as HomePage, restyled with Playfair + Manrope,
// linen/olive palette, magazine-style negative space.
// Mounted at /preview-v3 to allow A/B comparison.
// =========================================================

// Design tokens
const C = {
  bg: '#F5F5F0',
  surface: '#FAFAF6',
  ink: '#222222',
  ink2: '#555555',
  olive: '#4A533E',
  oliveSoft: '#6A7359',
  stone: '#D1CFC7',
  hairline: '#E5E1D8',
};

const HERO_IMG = 'https://static.prod-images.emergentagent.com/jobs/03019b9c-e38d-4235-95ae-da304b949bfe/images/f67d59e1e8162bff9a7e4a6b2480de329ed3178b24de41242cda120c45cfcfec.png';

const Serif = ({ children, className = '', as: As = 'span', ...rest }) => (
  <As
    className={className}
    style={{ fontFamily: '"Playfair Display", Georgia, serif', letterSpacing: '-0.01em' }}
    {...rest}
  >
    {children}
  </As>
);

const TextLink = ({ to, onClick, children, large = false }) => {
  const className = `group inline-flex items-center gap-2 ${large ? 'text-base' : 'text-sm'} font-medium tracking-wide transition-colors`;
  const content = (
    <>
      <span className="border-b border-current pb-0.5">{children}</span>
      <ArrowUpRight
        size={large ? 18 : 16}
        className="transition-transform duration-300 group-hover:translate-x-1 group-hover:-translate-y-1"
      />
    </>
  );
  const style = { color: C.olive, fontFamily: 'Manrope, sans-serif' };
  if (onClick) return <button type="button" onClick={onClick} className={className} style={style}>{content}</button>;
  return <Link to={to} className={className} style={style}>{content}</Link>;
};

const Overline = ({ children }) => (
  <p
    className="uppercase tracking-[0.3em] text-xs"
    style={{ color: C.oliveSoft, fontFamily: 'Manrope, sans-serif' }}
  >
    {children}
  </p>
);

const HomePagePreviewV3 = () => {
  const navigate = useNavigate();

  const conditions = [
    { num: '01', icon: AlertCircle, title: "Hyperventilation", description: "Reconnaître et traiter le syndrome d'hyperventilation chronique.", link: "/hyperventilation" },
    { num: '02', icon: Wind, title: "Respiration Dysfonctionnelle", description: "Comprendre les patterns respiratoires inadaptés et les corriger.", link: "/respiration-dysfonctionnelle" },
    { num: '03', icon: Waves, title: "Asthme", description: "Techniques respiratoires pour gérer et réduire les symptômes.", link: "/asthme" },
    { num: '04', icon: Activity, title: "COVID Long", description: "Rééducation respiratoire pour les symptômes persistants post-COVID.", link: "/covid-long" },
  ];

  const educationalResources = [
    { icon: Brain, title: "Causes des Troubles Respiratoires", description: "Le rôle du système nerveux autonome.", link: "/causes-troubles-respiratoires" },
    { icon: Layers, title: "Mécanique Respiratoire", description: "Anatomie et physiologie de la respiration.", link: "/mecanique-respiratoire" },
    { icon: Sparkles, title: "Respiration Optimale", description: "Les six principes fondamentaux et leurs bénéfices.", link: "/respiration-optimale" },
    { icon: Target, title: "Méthode Buteyko", description: "Améliorer la tolérance au CO₂.", link: "/methode-buteyko" },
    { icon: Heart, title: "Cohérence Cardiaque", description: "Harmoniser cœur, cerveau et émotions.", link: "/coherence-cardiaque" },
    { icon: Activity, title: "Ma Chaîne YouTube", description: "Vidéos d'exercices et de rééducation.", link: "/youtube" },
    { icon: ClipboardList, title: "Évaluer Votre Respiration", description: "BOLT, Nijmegen, fréquence respiratoire.", link: "/evaluer-respiration" },
  ];

  const benefits = [
    "Réduction des symptômes respiratoires",
    "Amélioration de la tolérance à l'effort",
    "Diminution de l'anxiété",
    "Meilleur sommeil",
    "Équilibre du système nerveux",
    "Augmentation de l'énergie",
    "Réduction de l'inflammation",
    "Amélioration de la concentration",
  ];

  return (
    <div
      className="min-h-screen"
      style={{
        backgroundColor: C.bg,
        color: C.ink,
        fontFamily: 'Manrope, sans-serif',
      }}
      data-testid="homepage-v3"
    >
      {/* ============ GLASSMORPHIC NAV ============ */}
      <header
        className="sticky top-0 z-40 border-b"
        style={{
          backgroundColor: 'rgba(245, 245, 240, 0.72)',
          backdropFilter: 'saturate(180%) blur(20px)',
          WebkitBackdropFilter: 'saturate(180%) blur(20px)',
          borderColor: C.hairline,
        }}
      >
        <div className="max-w-[1400px] mx-auto px-8 py-5 flex items-center justify-between">
          <Link to="/" className="flex items-center gap-3">
            <img src="/julien-jaccard.png" alt="Julien Jaccard" className="w-10 h-10 rounded-full object-cover" />
            <div className="leading-tight">
              <Serif as="div" className="text-lg" style={{ color: C.ink, fontFamily: '"Playfair Display", serif' }}>
                Julien Jaccard
              </Serif>
              <span className="text-[10px] uppercase tracking-[0.25em]" style={{ color: C.ink2 }}>
                Physiothérapie · respiration
              </span>
            </div>
          </Link>
          <nav className="hidden md:flex items-center gap-10 text-sm" style={{ color: C.ink2 }}>
            <Link to="/" className="hover:text-black transition-colors">Accueil</Link>
            <Link to="/causes-troubles-respiratoires" className="hover:text-black transition-colors">Comprendre</Link>
            <Link to="/coherence-cardiaque" className="hover:text-black transition-colors">Techniques</Link>
            <Link to="/evaluer-respiration" className="hover:text-black transition-colors">Tester</Link>
            <Link to="/contact" className="hover:text-black transition-colors">Contact</Link>
          </nav>
          <TextLink to="/app-landing">Accéder aux exercices</TextLink>
        </div>
      </header>

      {/* ============ HERO ============ */}
      <section className="relative">
        <div className="max-w-[1400px] mx-auto px-8 pt-24 pb-32">
          <div className="grid grid-cols-1 lg:grid-cols-12 gap-12 items-start">
            {/* Headline & intro */}
            <div className="lg:col-span-6 lg:pt-12">
              <Overline>Méthode · Rééducation respiratoire</Overline>
              <Serif as="h1" className="mt-8 text-[clamp(2.75rem,5.5vw,5rem)] leading-[1.02] font-medium">
                La rééducation
                <br />
                <em className="italic" style={{ color: C.olive }}>des troubles</em>
                <br />
                respiratoires.
              </Serif>
              <p className="mt-10 text-lg leading-relaxed max-w-xl" style={{ color: C.ink2 }}>
                Hyperventilation, respiration dysfonctionnelle, asthme, COVID long&nbsp;: des techniques scientifiquement prouvées pour retrouver un souffle juste et un système nerveux apaisé.
              </p>
              <div className="mt-12 flex flex-col sm:flex-row items-start sm:items-center gap-x-10 gap-y-6">
                <TextLink onClick={() => navigate('/app-landing')} large>Commencer les exercices</TextLink>
                <TextLink onClick={() => navigate('/evaluer-respiration')} large>Tester sa respiration</TextLink>
              </div>
            </div>

            {/* Floating image */}
            <div className="lg:col-span-6 relative">
              <div
                className="relative aspect-[4/5] w-full overflow-hidden"
                style={{
                  boxShadow: '0 40px 80px -30px rgba(34,34,34,0.25)',
                }}
              >
                <img src={HERO_IMG} alt="Espace de respiration paisible" className="w-full h-full object-cover" />
              </div>
              <div
                className="absolute -bottom-8 -left-8 px-7 py-5 hidden md:block"
                style={{
                  backgroundColor: C.surface,
                  border: `1px solid ${C.hairline}`,
                }}
              >
                <Overline>Depuis 1999</Overline>
                <Serif as="p" className="text-2xl mt-2">25 ans d&apos;expérience</Serif>
              </div>
            </div>
          </div>
        </div>

        {/* Hairline */}
        <div className="max-w-[1400px] mx-auto px-8">
          <div className="h-px" style={{ backgroundColor: C.hairline }} />
        </div>
      </section>

      {/* ============ ABOUT ============ */}
      <section className="py-32">
        <div className="max-w-[1100px] mx-auto px-8">
          <div className="grid grid-cols-1 md:grid-cols-12 gap-12">
            <div className="md:col-span-3">
              <Overline>À propos</Overline>
            </div>
            <div className="md:col-span-9">
              <Serif as="p" className="text-2xl md:text-3xl leading-[1.35] font-normal">
                <em className="italic">«&nbsp;Les symptômes que nous ressentons ne sont pas des problèmes, mais des solutions que le corps trouve pour s&apos;adapter. La respiration est l&apos;une des portes pour retrouver l&apos;équilibre.&nbsp;»</em>
              </Serif>
              <p className="mt-10 leading-relaxed text-lg" style={{ color: C.ink2 }}>
                Physiothérapeute depuis 1999, je me suis passionné pour les troubles respiratoires depuis cinq ans. J&apos;ai étudié et pratiqué sur moi-même toutes les techniques respiratoires, et développé une compréhension organique de cette machine humaine qui me permet de <strong style={{ color: C.ink }}>vous comprendre</strong>, d&apos;identifier les paramètres uniques de votre vie qui influencent son équilibre.
              </p>
            </div>
          </div>
        </div>
      </section>

      {/* ============ CONDITIONS — Bento numbered ============ */}
      <section className="py-32" style={{ borderTop: `1px solid ${C.hairline}`, borderBottom: `1px solid ${C.hairline}` }}>
        <div className="max-w-[1400px] mx-auto px-8">
          <div className="flex items-end justify-between flex-wrap gap-6 mb-20">
            <div>
              <Overline>Troubles traités</Overline>
              <Serif as="h2" className="mt-6 text-4xl md:text-5xl leading-tight">
                Quatre familles<br />de dysfonctions.
              </Serif>
            </div>
            <p className="max-w-md text-sm leading-relaxed" style={{ color: C.ink2 }}>
              Une approche personnalisée pour chaque condition, fondée sur les dernières avancées en physiothérapie respiratoire.
            </p>
          </div>
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4">
            {conditions.map((c, i) => (
              <button
                key={c.title}
                onClick={() => navigate(c.link)}
                className="text-left p-10 group transition-colors duration-300"
                style={{
                  borderRight: i < 3 ? `1px solid ${C.hairline}` : 'none',
                  borderBottom: i < 2 ? `1px solid ${C.hairline}` : 'none',
                }}
              >
                <div className="flex items-center justify-between mb-12">
                  <span className="text-xs tracking-[0.3em]" style={{ color: C.oliveSoft }}>{c.num}</span>
                  <c.icon size={20} strokeWidth={1.5} style={{ color: C.olive }} />
                </div>
                <Serif as="h3" className="text-2xl mb-4 leading-snug">{c.title}</Serif>
                <p className="text-sm leading-relaxed mb-8" style={{ color: C.ink2 }}>{c.description}</p>
                <span
                  className="inline-flex items-center gap-2 text-xs uppercase tracking-[0.2em] transition-transform duration-300 group-hover:translate-x-1"
                  style={{ color: C.olive }}
                >
                  En savoir plus →
                </span>
              </button>
            ))}
          </div>
        </div>
      </section>

      {/* ============ EDUCATIONAL RESOURCES ============ */}
      <section className="py-32">
        <div className="max-w-[1400px] mx-auto px-8">
          <div className="grid grid-cols-1 md:grid-cols-12 gap-12 mb-20">
            <div className="md:col-span-4">
              <Overline>Comprendre</Overline>
              <Serif as="h2" className="mt-6 text-4xl md:text-5xl leading-tight">
                Ressources<br />éducatives.
              </Serif>
            </div>
            <p className="md:col-span-6 md:col-start-7 text-base leading-relaxed self-end" style={{ color: C.ink2 }}>
              Explorez les fondamentaux du système respiratoire&nbsp;: anatomie, physiologie, techniques, et outils d&apos;auto-évaluation.
            </p>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-x-12 gap-y-16">
            {educationalResources.map((r, i) => (
              <button
                key={r.title}
                onClick={() => navigate(r.link)}
                className="text-left group"
              >
                <div className="flex items-center gap-3 mb-8" style={{ color: C.olive }}>
                  <span className="text-xs tabular-nums tracking-[0.3em]" style={{ color: C.oliveSoft }}>
                    {String(i + 1).padStart(2, '0')}
                  </span>
                  <div className="flex-1 h-px" style={{ backgroundColor: C.hairline }} />
                  <r.icon size={18} strokeWidth={1.5} />
                </div>
                <Serif as="h3" className="text-2xl leading-snug mb-3 transition-colors group-hover:opacity-70">
                  {r.title}
                </Serif>
                <p className="text-sm leading-relaxed mb-6" style={{ color: C.ink2 }}>
                  {r.description}
                </p>
                <span className="inline-flex items-center gap-2 text-xs uppercase tracking-[0.2em] transition-transform duration-300 group-hover:translate-x-1" style={{ color: C.olive }}>
                  Lire →
                </span>
              </button>
            ))}
          </div>
        </div>
      </section>

      {/* ============ BENEFITS ============ */}
      <section className="py-32" style={{ backgroundColor: C.surface, borderTop: `1px solid ${C.hairline}`, borderBottom: `1px solid ${C.hairline}` }}>
        <div className="max-w-[1100px] mx-auto px-8">
          <div className="grid md:grid-cols-12 gap-12 items-start">
            <div className="md:col-span-5">
              <Overline>Bénéfices</Overline>
              <Serif as="h2" className="mt-6 text-4xl md:text-5xl leading-tight">
                Ce que la respiration <em className="italic" style={{ color: C.olive }}>change</em>.
              </Serif>
              <p className="mt-8 text-base leading-relaxed" style={{ color: C.ink2 }}>
                Vingt mille respirations par jour. Une fonction vitale dont la justesse rayonne sur l&apos;ensemble du corps et de l&apos;esprit.
              </p>
            </div>
            <ul className="md:col-span-7 grid grid-cols-1 sm:grid-cols-2">
              {benefits.map((b, i) => (
                <li
                  key={b}
                  className="py-5 flex items-baseline gap-4"
                  style={{
                    borderTop: i < 2 ? `1px solid ${C.hairline}` : 'none',
                    borderBottom: `1px solid ${C.hairline}`,
                  }}
                >
                  <span className="text-xs tabular-nums tracking-widest" style={{ color: C.oliveSoft }}>
                    {String(i + 1).padStart(2, '0')}
                  </span>
                  <span className="text-base leading-snug" style={{ color: C.ink }}>{b}</span>
                </li>
              ))}
            </ul>
          </div>
        </div>
      </section>

      {/* ============ AVERTISSEMENT (subtle editorial note) ============ */}
      <section className="py-24">
        <div className="max-w-[900px] mx-auto px-8">
          <div className="flex items-start gap-6 py-10 px-8" style={{ border: `1px solid ${C.hairline}` }}>
            <span className="text-xs uppercase tracking-[0.3em] mt-1.5 whitespace-nowrap" style={{ color: C.olive }}>
              Avertissement
            </span>
            <p className="text-sm leading-relaxed" style={{ color: C.ink2 }}>
              Les informations et exercices décrits ici sont à titre informatif. Les problématiques évoquées relèvent d&apos;un diagnostic médical et d&apos;un accompagnement par un professionnel de santé spécialisé. Ces pages résument succinctement le contenu de séances thérapeutiques individualisées.{' '}
              <Link to="/contact" className="underline" style={{ color: C.olive }}>Me contacter</Link>.
            </p>
          </div>
        </div>
      </section>

      {/* ============ CTA ============ */}
      <section className="py-40" style={{ backgroundColor: C.olive, color: C.bg }}>
        <div className="max-w-[1100px] mx-auto px-8 text-center">
          <Overline>
            <span style={{ color: C.stone }}>Commencer</span>
          </Overline>
          <Serif as="h2" className="mt-8 text-5xl md:text-6xl lg:text-7xl leading-[1.05]">
            Prêt à <em className="italic">retrouver</em><br />votre souffle ?
          </Serif>
          <p className="mt-10 text-lg max-w-xl mx-auto leading-relaxed" style={{ color: 'rgba(245,245,240,0.75)' }}>
            Accédez gratuitement aux exercices guidés et commencez votre parcours respiratoire dès aujourd&apos;hui.
          </p>
          <div className="mt-14 flex flex-col sm:flex-row items-center justify-center gap-x-12 gap-y-6">
            <button
              onClick={() => navigate('/app-landing')}
              className="group inline-flex items-center gap-2 text-base font-medium tracking-wide"
              style={{ color: C.bg, fontFamily: 'Manrope, sans-serif' }}
            >
              <span className="border-b border-current pb-0.5">Commencer maintenant</span>
              <ArrowUpRight size={18} className="transition-transform duration-300 group-hover:translate-x-1 group-hover:-translate-y-1" />
            </button>
            <button
              onClick={() => navigate('/youtube')}
              className="group inline-flex items-center gap-2 text-base font-medium tracking-wide"
              style={{ color: 'rgba(245,245,240,0.8)', fontFamily: 'Manrope, sans-serif' }}
            >
              <Youtube size={16} />
              <span className="border-b border-current pb-0.5">Chaîne YouTube</span>
            </button>
          </div>
        </div>
      </section>

      {/* ============ FOOTER ============ */}
      <footer className="py-16" style={{ borderTop: `1px solid ${C.hairline}` }}>
        <div className="max-w-[1400px] mx-auto px-8 flex flex-col md:flex-row items-start md:items-center justify-between gap-8">
          <div>
            <Serif as="div" className="text-xl" style={{ color: C.ink }}>Julien Jaccard</Serif>
            <p className="text-sm mt-1" style={{ color: C.ink2 }}>Physiothérapie · Rééducation respiratoire</p>
          </div>
          <div className="flex flex-col md:flex-row gap-x-10 gap-y-2 text-sm" style={{ color: C.ink2 }}>
            <a href="mailto:julienjaccard@mains-nuages.ch" style={{ color: C.olive }} className="underline-offset-4 hover:underline">
              julienjaccard@mains-nuages.ch
            </a>
            <Link to="/contact" className="hover:text-black transition-colors">Contact</Link>
            <Link to="/documents" className="hover:text-black transition-colors">Documents</Link>
          </div>
          <p className="text-xs uppercase tracking-[0.25em]" style={{ color: C.oliveSoft }}>
            © {new Date().getFullYear()} · Mains Nuages
          </p>
        </div>
      </footer>
    </div>
  );
};

export default HomePagePreviewV3;
